import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    lookupAccountManagers,
    lookupAccountManagersFailure,
    lookupAccountManagersSuccess,
    lookupBizDevManagers,
    lookupBizDevManagersFailure,
    lookupBizDevManagersSuccess,
    lookupBrands,
    lookupBrandsFailure,
    lookupBrandsSuccess,
    lookupContractedGroups,
    lookupContractedGroupsFailure,
    lookupContractedGroupsSuccess,
    lookupFacilityTypes,
    lookupFacilityTypesFailure,
    lookupFacilityTypesSuccess,
    lookupHospitalSystems,
    lookupHospitalSystemsFailure,
    lookupHospitalSystemsHierarchy,
    lookupHospitalSystemsHierarchyFailure,
    lookupHospitalSystemsHierarchySuccess,
    lookupHospitalSystemsSuccess,
    lookupStatesFailure,
    lookupStatesSuccess,
    lookupMSPManagers,
    lookupMSPManagersFailure,
    lookupMSPManagersSuccess,
    lookupStates,
    lookupVMSProviders,
    lookupVMSProvidersFailure,
    lookupVMSProvidersSuccess
} from '../actions/workforce-solutions.actions';
import { selectStatesLookups } from '../workforce-solutions.selectors';
import { HospitalsService } from 'src/app/shared/services/hospitals.service';
import { FacilityContractsService } from '../../../facility-contracts/facility-contracts.service';

@Injectable()
export class WorkforceSolutionsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _hospitalService: HospitalsService,
        private readonly _store: Store,
        private readonly _facilityContractsService: FacilityContractsService
    ) {}

    loadAccountManagers$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupAccountManagers),
            switchMap((action) => {
                return this._hospitalService.lookupAccountManager(action.filterText).pipe(
                    map((accountManagers) => lookupAccountManagersSuccess({ accountManagers })),
                    catchError((error) => of(lookupAccountManagersFailure({ error })))
                );
            })
        );
    });

    lookupVMSProviders$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupVMSProviders),
            switchMap((action) => {
                return this._hospitalService.lookupVMSProvider(action.filterText).pipe(
                    map((vmsProviders) => lookupVMSProvidersSuccess({ vmsProviders })),
                    catchError((error) => of(lookupVMSProvidersFailure({ error })))
                );
            })
        );
    });

    lookupBizDevManagers$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupBizDevManagers),
            switchMap((action) => {
                return this._facilityContractsService.getBusinessDevelopmentManagers(action.filterText).pipe(
                    map((bizDevManagers) => lookupBizDevManagersSuccess({ bizDevManagers })),
                    catchError((error) => of(lookupBizDevManagersFailure({ error })))
                );
            })
        );
    });

    lookupMSPManagers$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupMSPManagers),
            switchMap((action) => {
                return this._facilityContractsService.getClientReviewReps(action.filterText).pipe(
                    map((mspManagers) => lookupMSPManagersSuccess({ mspManagers })),
                    catchError((error) => of(lookupMSPManagersFailure({ error })))
                );
            })
        );
    });

    lookupContractedGroups$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupContractedGroups),
            switchMap((action) => {
                return this._hospitalService.lookupContractedGroup(action.filterText).pipe(
                    map((contractedGroups) => lookupContractedGroupsSuccess({ contractedGroups })),
                    catchError((error) => of(lookupContractedGroupsFailure({ error })))
                );
            })
        );
    });

    lookupHospitalSystemss$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupHospitalSystems),
            switchMap((action) => {
                return this._hospitalService.lookupHospitalSystem(action.filterText).pipe(
                    map((hospitalSystems) => lookupHospitalSystemsSuccess({ hospitalSystems })),
                    catchError((error) => of(lookupHospitalSystemsFailure({ error })))
                );
            })
        );
    });

    lookupHospitalSystemsHierarchy$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupHospitalSystemsHierarchy),
            switchMap(() => {
                return this._hospitalService.lookupHospitalSystemsHierarchy().pipe(
                    map((hospitalSystemsHierarchy) =>
                        lookupHospitalSystemsHierarchySuccess({ hospitalSystemsHierarchy })
                    ),
                    catchError((error) => of(lookupHospitalSystemsHierarchyFailure({ error })))
                );
            })
        );
    });

    lookupBrands$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupBrands),
            switchMap(() => {
                return this._hospitalService.getBrands().pipe(
                    map((brands) => lookupBrandsSuccess({ brands })),
                    catchError((error) => of(lookupBrandsFailure({ error })))
                );
            })
        );
    });

    lookupFacilityTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupFacilityTypes),
            switchMap(() => {
                return this._hospitalService.lookupFacilityType().pipe(
                    map((facilityTypes) => lookupFacilityTypesSuccess({ facilityTypes })),
                    catchError((error) => of(lookupFacilityTypesFailure({ error })))
                );
            })
        );
    });

    lookupStates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(lookupStates),
            concatLatestFrom(() => this._store.select(selectStatesLookups)),
            switchMap(([_, states]) => {
                if (states) {
                    return of(lookupStatesSuccess({ states }));
                }

                return this._hospitalService.lookupStates().pipe(
                    map((states) => lookupStatesSuccess({ states })),
                    catchError((error) => of(lookupStatesFailure({ error })))
                );
            })
        );
    });
}
